/**
 * Operations Dashboard
 * - Live call statistics (completed, missed, in-progress)
 * - Real-time alerts (RED flags, missed calls, transcription failures, late observers)
 * - Auto-refresh every 30 seconds
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Phone, PhoneCall, PhoneOff, PhoneMissed, Clock, Users, 
  AlertTriangle, AlertCircle, AlertOctagon, CheckCircle,
  RefreshCw, TrendingUp, Activity, Bell, Shield, FileText,
  User, Baby, Timer, XCircle, Loader2, Eye
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Auto-refresh interval (30 seconds)
const REFRESH_INTERVAL = 30000;

const OperationsDashboard = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  
  // Dashboard data
  const [callStats, setCallStats] = useState({});
  const [alerts, setAlerts] = useState([]);
  const [alertCounts, setAlertCounts] = useState({});
  const [severityCounts, setSeverityCounts] = useState({});
  const [liveCalls, setLiveCalls] = useState([]);
  const [transcriptionStats, setTranscriptionStats] = useState({});
  const [safetyStats, setSafetyStats] = useState({});
  const [hourlyData, setHourlyData] = useState([]);

  const getToken = () => localStorage.getItem('admin_token') || localStorage.getItem('principal_token') || 'admin';

  const loadDashboard = useCallback(async (showRefresh = false) => {
    try {
      if (showRefresh) setRefreshing(true);
      
      const token = getToken();
      
      const [dashboardRes, alertsRes, liveCallsRes] = await Promise.all([
        axios.get(`${API_URL}/api/operations/dashboard?token=${token}`),
        axios.get(`${API_URL}/api/operations/alerts?token=${token}&limit=20`),
        axios.get(`${API_URL}/api/operations/live-calls?token=${token}`)
      ]);
      
      setCallStats(dashboardRes.data.call_stats || {});
      setHourlyData(dashboardRes.data.hourly_breakdown || []);
      setTranscriptionStats(dashboardRes.data.transcription_stats || {});
      setSafetyStats(dashboardRes.data.safety_summary || {});
      
      setAlerts(alertsRes.data.alerts || []);
      setAlertCounts(alertsRes.data.counts_by_type || {});
      setSeverityCounts(alertsRes.data.counts_by_severity || {});
      
      setLiveCalls(liveCallsRes.data.active_calls || []);
      
      setLastUpdated(new Date());
      
    } catch (err) {
      console.error('Error loading dashboard:', err);
      if (!loading) {
        toast({
          title: "Refresh Failed",
          description: "Could not update dashboard data",
          variant: "destructive"
        });
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [loading, toast]);

  useEffect(() => {
    loadDashboard();
    
    // Auto-refresh
    const interval = setInterval(() => loadDashboard(false), REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [loadDashboard]);

  const acknowledgeAlert = async (alertId) => {
    try {
      const token = getToken();
      await axios.post(`${API_URL}/api/operations/alerts/${alertId}/acknowledge?token=${token}`);
      toast({ title: "Alert Acknowledged" });
      loadDashboard(true);
    } catch (err) {
      toast({ title: "Error", description: "Could not acknowledge alert", variant: "destructive" });
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-300';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'low': return 'bg-blue-100 text-blue-800 border-blue-300';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case 'red_flag': return <AlertOctagon className="w-5 h-5 text-red-600" />;
      case 'missed_call': return <PhoneMissed className="w-5 h-5 text-orange-600" />;
      case 'transcription_failure': return <FileText className="w-5 h-5 text-purple-600" />;
      case 'late_observer': return <Timer className="w-5 h-5 text-yellow-600" />;
      case 'pending_notes': return <FileText className="w-5 h-5 text-blue-600" />;
      default: return <AlertCircle className="w-5 h-5 text-gray-600" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-green-500 mx-auto mb-4" />
          <p className="text-slate-400">Loading Operations Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-white p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-3">
            <Activity className="w-8 h-8 text-green-500" />
            Operations Dashboard
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            Last updated: {lastUpdated ? lastUpdated.toLocaleTimeString() : 'Never'}
          </p>
        </div>
        <Button 
          onClick={() => loadDashboard(true)} 
          variant="outline"
          className="border-slate-600 text-slate-300 hover:bg-slate-800"
          disabled={refreshing}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Live Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-4 text-center">
            <Phone className="w-6 h-6 text-slate-400 mx-auto mb-2" />
            <p className="text-3xl font-bold text-white">{callStats.total_scheduled || 0}</p>
            <p className="text-xs text-slate-400">Total Scheduled</p>
          </CardContent>
        </Card>
        
        <Card className="bg-green-900/50 border-green-700">
          <CardContent className="p-4 text-center">
            <CheckCircle className="w-6 h-6 text-green-400 mx-auto mb-2" />
            <p className="text-3xl font-bold text-green-400">{callStats.completed || 0}</p>
            <p className="text-xs text-green-300">Completed</p>
          </CardContent>
        </Card>
        
        <Card className="bg-blue-900/50 border-blue-700">
          <CardContent className="p-4 text-center">
            <PhoneCall className="w-6 h-6 text-blue-400 mx-auto mb-2 animate-pulse" />
            <p className="text-3xl font-bold text-blue-400">{callStats.in_progress || 0}</p>
            <p className="text-xs text-blue-300">In Progress</p>
          </CardContent>
        </Card>
        
        <Card className="bg-yellow-900/50 border-yellow-700">
          <CardContent className="p-4 text-center">
            <Clock className="w-6 h-6 text-yellow-400 mx-auto mb-2" />
            <p className="text-3xl font-bold text-yellow-400">{callStats.pending || 0}</p>
            <p className="text-xs text-yellow-300">Pending</p>
          </CardContent>
        </Card>
        
        <Card className="bg-red-900/50 border-red-700">
          <CardContent className="p-4 text-center">
            <PhoneOff className="w-6 h-6 text-red-400 mx-auto mb-2" />
            <p className="text-3xl font-bold text-red-400">{callStats.missed || 0}</p>
            <p className="text-xs text-red-300">Missed</p>
          </CardContent>
        </Card>
        
        <Card className="bg-purple-900/50 border-purple-700">
          <CardContent className="p-4 text-center">
            <TrendingUp className="w-6 h-6 text-purple-400 mx-auto mb-2" />
            <p className="text-3xl font-bold text-purple-400">{callStats.completion_rate || 0}%</p>
            <p className="text-xs text-purple-300">Completion Rate</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Live Calls Panel */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2 text-white">
              <PhoneCall className="w-5 h-5 text-green-500 animate-pulse" />
              Live Calls ({liveCalls.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {liveCalls.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <PhoneOff className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No active calls</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[300px] overflow-y-auto">
                {liveCalls.map((call, idx) => (
                  <div 
                    key={call.id || idx}
                    className="p-3 bg-green-900/30 rounded-lg border border-green-700"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-white">{call.child_name}</p>
                        <p className="text-sm text-slate-400">{call.observer_name}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-mono text-green-400">
                          {formatDuration(call.current_duration)}
                        </p>
                        <p className="text-xs text-slate-500">Duration</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Alerts Panel */}
        <Card className="bg-slate-800 border-slate-700 lg:col-span-2">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2 text-white">
                <Bell className="w-5 h-5 text-orange-500" />
                Active Alerts ({alerts.length})
              </CardTitle>
              <div className="flex gap-2">
                {severityCounts.critical > 0 && (
                  <span className="px-2 py-1 bg-red-600 text-white text-xs rounded-full">
                    {severityCounts.critical} Critical
                  </span>
                )}
                {severityCounts.high > 0 && (
                  <span className="px-2 py-1 bg-orange-600 text-white text-xs rounded-full">
                    {severityCounts.high} High
                  </span>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {alerts.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500 opacity-50" />
                <p>No active alerts</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[350px] overflow-y-auto">
                {alerts.map((alert, idx) => (
                  <div 
                    key={alert.id || idx}
                    className={`p-3 rounded-lg border ${getSeverityColor(alert.severity)}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        {getAlertIcon(alert.type)}
                        <div>
                          <p className="font-medium">{alert.title}</p>
                          <p className="text-sm opacity-75">{alert.message}</p>
                          {alert.child_name && (
                            <p className="text-xs mt-1 opacity-60">
                              Child: {alert.child_name}
                              {alert.observer_name && ` • Observer: ${alert.observer_name}`}
                            </p>
                          )}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => acknowledgeAlert(alert.id)}
                        className="text-xs"
                      >
                        <Eye className="w-3 h-3 mr-1" />
                        Ack
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Alert Type Summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-6">
        <Card className="bg-red-900/30 border-red-700">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertOctagon className="w-8 h-8 text-red-500" />
            <div>
              <p className="text-2xl font-bold text-red-400">{alertCounts.red_flag || 0}</p>
              <p className="text-xs text-red-300">RED Flags</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-orange-900/30 border-orange-700">
          <CardContent className="p-4 flex items-center gap-3">
            <PhoneMissed className="w-8 h-8 text-orange-500" />
            <div>
              <p className="text-2xl font-bold text-orange-400">{alertCounts.missed_call || 0}</p>
              <p className="text-xs text-orange-300">Missed Calls</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-purple-900/30 border-purple-700">
          <CardContent className="p-4 flex items-center gap-3">
            <FileText className="w-8 h-8 text-purple-500" />
            <div>
              <p className="text-2xl font-bold text-purple-400">{alertCounts.transcription_failure || 0}</p>
              <p className="text-xs text-purple-300">Transcription Fails</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-yellow-900/30 border-yellow-700">
          <CardContent className="p-4 flex items-center gap-3">
            <Timer className="w-8 h-8 text-yellow-500" />
            <div>
              <p className="text-2xl font-bold text-yellow-400">{alertCounts.late_observer || 0}</p>
              <p className="text-xs text-yellow-300">Late Observers</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-blue-900/30 border-blue-700">
          <CardContent className="p-4 flex items-center gap-3">
            <FileText className="w-8 h-8 text-blue-500" />
            <div>
              <p className="text-2xl font-bold text-blue-400">{alertCounts.pending_notes || 0}</p>
              <p className="text-xs text-blue-300">Pending Notes</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Safety & Transcription Stats */}
      <div className="grid md:grid-cols-2 gap-6 mt-6">
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2 text-white">
              <Shield className="w-5 h-5 text-red-500" />
              Safety Flags
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="text-center p-3 bg-red-900/30 rounded-lg">
                <p className="text-2xl font-bold text-red-400">{safetyStats.red_open || 0}</p>
                <p className="text-xs text-red-300">🔴 RED Open</p>
              </div>
              <div className="text-center p-3 bg-orange-900/30 rounded-lg">
                <p className="text-2xl font-bold text-orange-400">{safetyStats.orange_open || 0}</p>
                <p className="text-xs text-orange-300">🟠 Orange Open</p>
              </div>
              <div className="text-center p-3 bg-yellow-900/30 rounded-lg">
                <p className="text-2xl font-bold text-yellow-400">{safetyStats.yellow_open || 0}</p>
                <p className="text-xs text-yellow-300">🟡 Yellow Open</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2 text-white">
              <FileText className="w-5 h-5 text-purple-500" />
              Transcription Queue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="text-center p-3 bg-slate-700 rounded-lg">
                <p className="text-2xl font-bold text-slate-300">{transcriptionStats.pending || 0}</p>
                <p className="text-xs text-slate-400">Pending</p>
              </div>
              <div className="text-center p-3 bg-blue-900/30 rounded-lg">
                <p className="text-2xl font-bold text-blue-400">{transcriptionStats.processing || 0}</p>
                <p className="text-xs text-blue-300">Processing</p>
              </div>
              <div className="text-center p-3 bg-green-900/30 rounded-lg">
                <p className="text-2xl font-bold text-green-400">{transcriptionStats.completed_today || 0}</p>
                <p className="text-xs text-green-300">Done Today</p>
              </div>
              <div className="text-center p-3 bg-red-900/30 rounded-lg">
                <p className="text-2xl font-bold text-red-400">{transcriptionStats.failed || 0}</p>
                <p className="text-xs text-red-300">Failed</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default OperationsDashboard;
